import { useGetPurchaserSavedProductPricesQuery } from "@fero/hooks";
import { usePurchaserSalePriceStore } from "@fero/stores";
import { Button, Divider, Modal, Stack, Typography } from "@mui/material";
import type React from "react";
import { useEffect } from "react";
import { usePurchaserSalePriceHelpers } from "../hooks";
import { ProductSearchBar } from "./product-search-bar";
import { PurchaserDiscountsModalDiscountOptions } from "./purchaser-discounts-modal-discount-options";
import { PurchaserDiscountsModalTable } from "./purchaser-discounts-modal-table";

interface PurchaserDiscountsModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onSubmit?: () => void;
}

export const PurchaserDiscountsModal: React.FC<
  PurchaserDiscountsModalProps
> = ({ isOpen, onClose, onSubmit }) => {
  const { addSalePrice, removeSalePrice } = usePurchaserSalePriceHelpers();
  const {
    salePricesToSave,
    purchaserName,
    organisationId,
    purchaserId,
    setSalePricesToSave,
  } = usePurchaserSalePriceStore();

  const { refetch } = useGetPurchaserSavedProductPricesQuery({
    organisationId,
    purchaserId,
    onSuccess: (data) => {
      // NB Add any existing sale prices to the store
      setSalePricesToSave(data.body);
    },
  });

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Stack
        justifyContent={"space-between"}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80vw",
          height: "70vh",
          // Scrolls on overflow rather than hanging off the bottom
          overflowY: "auto",
          backgroundColor: (theme) => theme.palette.background.paper,
          borderRadius: 1,
          padding: 4,
        }}
      >
        <Stack spacing={2} display={"flex"} flex={1}>
          <Typography variant="h6" fontSize={20}>
            Edit Discounts for {purchaserName}
          </Typography>

          <Divider />
          <Typography variant="h6" fontSize={20}>
            Default Discount
          </Typography>
          <Typography>
            Please indicate the default discount percentage to be applied for
            this purchaser.
          </Typography>
          <PurchaserDiscountsModalDiscountOptions />
          <Divider />
          <Typography variant="h6" fontSize={20}>
            Custom Price List
          </Typography>
          <Typography>
            The following custom prices will be automatically applied when
            creating an order for this purchaser.
          </Typography>
          <ProductSearchBar
            selectedProductCodes={
              new Set(salePricesToSave.map((product) => product.productCode))
            }
            onSelectProduct={(product) => {
              addSalePrice({
                productId: product.id,
                productCode: product.value.code,
                currency: "GBP",
                exDutyAndVatPrice: "0.00",
                incDutyAndVatPrice: "0.00",
                incDutyExVatPrice: "0.00",
              });
            }}
          />
          <PurchaserDiscountsModalTable
            discountedProductIds={salePricesToSave.map((p) => p.productId)}
            onRemoveDiscount={removeSalePrice}
          />
        </Stack>
        <Stack
          direction={"row"}
          spacing={2}
          justifyContent={"flex-end"}
          justifySelf={"flex-end"}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit}>Submit</Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
